/**
 * USE_VARIATION_API - API Integration Hook
 * 
 * SUMMARY:
 * Handles all API operations for variations including loading, saving, and data formatting.
 * Manages initialization, prefilling, and submission workflows.
 * 
 * @param {Object} params - Hook parameters
 * @returns {Object} API handlers and state
 */

import { useDispatch } from 'react-redux';
import idx from 'idx';
import _ from 'lodash';
import { addTaskAction, updateProfitMarginAction } from '../../../../slice/add-task';
import { getLaborList, getUnits } from '../../../../actions/inventory.action';
import { addResourcesAction } from '../../../../slice/resources';
import { errorSnackBar } from '../../../../actions/common.action';
import { subTaskCalculation } from '../../../../util/calculations/calculate-sub-task';
import {
    addOption,
    getOption,
    updateOption,
    updateTask,
    addSubTask,
    updateSubTask,
    batchDeleteSubTasks as batchDeleteSubTasksAction
} from '../../../../actions/project-management.action';
import { warningSnackBar } from '../../../../actions/common.action';

export const useVariationApi = ({
    optionId,
    profitMargin,
    resources,
    projectSummary,
    fields,
    setFields,
    setTemplateQty,
    excludeFromFieldOps,
    setExcludeFromFieldOps,
    isFieldOpsInheritedExcluded,
    setTaskState,
    setUnits,
    subTaskArray,
    deleteSubTask,
    props
}) => {
    const dispatch = useDispatch();

    /**
     * Initialize data loading
     */
    const initLoad = async () => {
        if (!resources.list.length) loadLaborList();

        // Set profit margin
        setFields(prev => ({
            ...prev,
            profitMargin: { ...prev.profitMargin, value: profitMargin }
        }));

        dispatch(updateProfitMarginAction({
            profitMargin: profitMargin,
            profitMarginType: 'PERCENTAGE',
        }));

        // Load units
        dispatch(getUnits({ sortBy: "name" })).then(res => {
            setUnits(res);
        }).catch(e => console.error(e));

        // Load existing variation if editing
        if (optionId && props.editOption) {
            dispatch(getOption(optionId)).then((data) => {
                preFill(data);
                setTaskState(prev => ({
                    ...prev,
                    showlockIcon: data.overrideProfitMargin == true ? false : true
                }));
                props.updateSelectedSection && props.updateSelectedSection(data.stageId);
            });
        } else {
            dispatch(addTaskAction({}));
        }
    };

    /**
     * Load labor list from API
     */
    const loadLaborList = () => {
        dispatch(getLaborList()).then((data) => {
            let labors = (data || []).map((labor, i) => {
                labor.sno = i + 1;
                return labor;
            });
            dispatch(addResourcesAction(labors));
        }).catch(e => console.error(e));
    };

    /**
     * Prefill form with existing variation data
     * 
     * @param {Object} variationData - Existing variation data
     */
    const preFill = (variationData) => {
        dispatch(addTaskAction(
            variationData,
            idx(projectSummary, _ => _.subcontractors) || []
        ));

        dispatch(updateProfitMarginAction({
            profitMargin: parseFloat(variationData.profitMargin || profitMargin || 0),
            profitMarginType: variationData.profitMarginType
        }));
        setExcludeFromFieldOps(!!variationData.excludeFromFieldOps);

        setFields(prev => ({
            ...prev,
            taskName: { ...prev.taskName, value: variationData.name },
            description: { ...prev.description, value: variationData.description || "" },
            date: { ...prev.date, value: new Date(variationData.date) },
            profitMarginType: { ...prev.profitMarginType, value: variationData.profitMarginType },
            profitMargin: { ...prev.profitMargin, value: parseFloat(variationData.profitMargin || profitMargin || 0) },
            attachments: {
                ...prev.attachments,
                attachmentArr: (variationData.attachment || []).map((attach) => {
                    return _.isString(attach) ? JSON.parse(attach) : attach;
                })
            }
        }));

        if (variationData.templateQuantity) {
            setTemplateQty(variationData.templateQuantity);
        }
    };

    /**
     * Format materials for API submission
     * 
     * @param {Array} materialsarray - Materials array
     * @returns {Array} Formatted materials
     */
    const formatNewMaterials = (materialsarray) => {
        return materialsarray.map((item) => ({
            item: item.name.value,
            id: item.id,
            categoryId: item.categoryId,
            manufacturer: item.manufacturer,
            partNo: item.partNo,
            cost: item.costPerItem.value,
            saleAmount: item.saleAmount.value,
            saleAmountLock: item.saleAmountLock.value,
            qty: item.qty.value,
            unit: item.unit.value || null,
            total: item.total.value,
            isInlineItem: item.isInlineItem || false,
        }));
    };

    /**
     * Format labor data for API submission
     * 
     * @param {Array} laborarray - Labor array
     * @returns {Array} Formatted labor data
     */
    const formatLaborData = (laborarray) => {
        const laborsList = idx(projectSummary, _ => _.labors) || [];
        const allLabors = idx(resources, _ => _.list) || [];

        return laborarray.map((_labor) => {
            const matchedLabor = laborsList.find((labor) => labor.id === _labor.id.value);
            let laborItem = allLabors.find((labor) => labor.id === _labor.id.value);

            if (!laborItem) laborItem = {};
            laborItem = _.cloneDeep(laborItem);
            laborItem['averageCost'] = idx(matchedLabor, _ => _.averageCost) || laborItem.cost;

            return {
                name: _labor.name.value,
                level: _labor.level.value,
                id: _labor.id.value,
                cost: _labor.costPerItem.value,
                qty: _labor.qty.value,
                calloutCost: _labor.calloutCost.value || 0,
                averageCost: (_labor.labourCost.value !== undefined && _labor.labourCost.value !== null && _labor.labourCost.value !== "")
                    ? _labor.labourCost.value
                    : (laborItem ? laborItem.averageCost : 0),
                is_personnel: _labor.is_personnel?.value ?? true,
                total: _labor.total.value,
            };
        });
    };

    /**
     * Format subcontractor data for API submission
     * 
     * @param {Array} subcontractorarray - Subcontractor array
     * @returns {Array} Formatted subcontractor data
     */
    const formatSubcontractorData = (subcontractorarray) => {
        return subcontractorarray.map((_subcontractor) => ({
            name: _subcontractor.name.value,
            id: _subcontractor.id.value,
            Cost: _subcontractor.Cost.value,
            saleAmount: _subcontractor.saleAmount.value,
            saleAmountLock: _subcontractor.saleAmountLock.value,
            description: _subcontractor.description.value,
        }));
    };

    /**
     * Submit handler - save variation
     * 
     * @param {Function} dialogClose - Close dialog callback
     */
    const submitHandler = async (dialogClose) => {
        const { projectId, editOption, action } = props;
        const _temp = _.cloneDeep(subTaskArray);

        // Validate all items have descriptions
        const allDescriptionsError = _temp.every((subtask) => subtask.descriptionError === false);
        if (_temp.length > 0 && !allDescriptionsError) {
            dispatch(errorSnackBar("A description is needed for all Items"));
            return;
        }

        const formData = new FormData();
        formData.set("name", fields.taskName.value);
        formData.set("profitMargin", fields.profitMargin.value || 0);
        formData.set("marginType", fields.profitMarginType.value);
        formData.set("date", fields.date.value); // Add date field
        if (isFieldOpsInheritedExcluded && !excludeFromFieldOps) {
            dispatch(
                warningSnackBar(
                    "This variation is excluded from field ops via its parent section and cannot be included here.",
                ),
            );
            setExcludeFromFieldOps(true);
        }
        formData.set(
            "excludeFromFieldOps",
            isFieldOpsInheritedExcluded ? true : excludeFromFieldOps,
        );
        // formData.set("date", fields.date.value); // Date not needed by endpoint

        const templateQty = fields.templateQuantity || '';
        if (templateQty) {
            formData.set("templateQuantity", templateQty);
        }

        // Append file attachments
        if (Array.isArray(fields.attachments.value)) {
            fields.attachments.value.forEach(file => {
                formData.append("attachment", file);
            });
        }

        let savedVariationId = optionId;

        // Handle save based on action
        if (optionId) {
            dispatch(updateTask(formData, optionId)).catch(e => console.error(e));

            // Delete removed subtasks using batch delete
            const idsToDelete = idx(deleteSubTask, _ => _.id) || [];
            if (idsToDelete.length > 0) {
                dispatch(batchDeleteSubTasksAction(idsToDelete)).catch(e => console.error(e));
            }
        }

        formData.set("project_id", projectId);

        if (props.stageId) {
            formData.set("stageId", props.stageId);
        }

        formData.set("isVariation", true);

        // Create or update variation
        if (!editOption || (editOption && action === "revise")) {
            if (optionId && action === "revise") {
                formData.set("isRevised", true);
                formData.set("optionId", optionId);
            }

            await dispatch(addOption(formData, "isVariation"))
                .then((res) => {
                    savedVariationId = res.id;
                })
                .catch((err) => console.error(err));
        } else {
            await dispatch(updateOption(formData, optionId, "isVariation"))
                .then((res) => {
                    savedVariationId = res.id;
                })
                .catch((e) => console.error(e));
        }

        // Save all subtasks
        if (savedVariationId) {
            let order = 1;
            // Map to track temporary IDs -> real IDs for nested sections
            const idMapping = {};

            for (const subtask of _temp) {
                // Check if this is a new item (no ID or temporary ID)
                const isNewItem = !subtask.id || subtask.id.toString().startsWith('temp-');

                // If parent has a temporary ID, replace it with the real ID
                let parentId = subtask.parent_subtask_id;
                if (parentId && parentId.toString().startsWith('temp-') && idMapping[parentId]) {
                    parentId = idMapping[parentId];
                }

                // Create a copy with updated parent ID if needed
                const subtaskToSave = parentId !== subtask.parent_subtask_id
                    ? { ...subtask, parent_subtask_id: parentId }
                    : subtask;

                if (action === "revise" || isNewItem) {
                    // Create new subtask
                    const result = await saveSubTask(subtaskToSave, savedVariationId, "addsubtask", order++);

                    // If this was a section with a temp ID, store the mapping
                    if (subtask.id && subtask.id.toString().startsWith('temp-') && result && result.id) {
                        idMapping[subtask.id] = result.id;
                    }
                } else {
                    // Update existing subtask
                    await saveSubTask(subtaskToSave, subtask.id, "updateSubTask", order++);
                }
            }

            dialogClose();
            if (props.getStages) props.getStages();
            if (props.getOptions) props.getOptions();
            if (props.getVariations) props.getVariations();
        }
    };

    /**
     * Save individual subtask
     * 
     * @param {Object} subtask - Subtask data
     * @param {string} id - Variation or subtask ID
     * @param {string} type - 'addsubtask' or 'updateSubTask'
     * @param {number} order - Sort order
     */
    const saveSubTask = async (subtask, id, type, order = 1) => {
        const formData = new FormData();
        const material = formatNewMaterials(subtask.material || []);
        const laborData = formatLaborData(subtask.labor || []);
        const subcontractorData = formatSubcontractorData(subtask.subcontractor || []);

        const calculation = subTaskCalculation({
            subtask: subtask,
            profitMargin: fields.profitMargin.value,
            profitMarginType: fields.profitMarginType.value,
            isForm: true
        });

        formData.set("description", subtask.description);
        formData.set("categoryId", subtask.categoryId || "");
        formData.set("order", order);
        formData.set("is_actual_materialcost", subtask.is_actual_materialcost);

        // Hierarchy fields for nested sections
        formData.set("is_section", subtask.is_section || false);

        // Only send parent_subtask_id if it's a real UUID (not temporary)
        if (subtask.parent_subtask_id && !subtask.parent_subtask_id.toString().startsWith('temp-')) {
            formData.set("parent_subtask_id", subtask.parent_subtask_id);
        }

        formData.set("labor_total_cost", calculation.labor.cost || 0);
        formData.set("laborTotal_SaleAmount", calculation.labor.sale || 0);
        formData.set("labor_profit", calculation.labor.profit || 0);

        formData.set("materialTotal_Cost", parseFloat(calculation.material.cost || 0));
        formData.set("materialTotal_SaleAmount", parseFloat(calculation.material.sale || 0));
        formData.set("material_Profit", parseFloat(calculation.material.profit || 0));

        formData.set("subcontractorTotal_Cost", calculation.subcontractor.cost || 0);
        formData.set("subcontractorTotal_SaleAmount", calculation.subcontractor.sale || 0);
        formData.set("subcontractor_Profit", calculation.subcontractor.profit || 0);

        formData.set("profitMargin", fields.profitMargin.value || 0);
        formData.set("marginType", fields.profitMarginType.value);
        formData.set("profitMarginCost", calculation.total_profit || 0);
        formData.set("totalProfit", calculation.total_profit || 0);
        formData.set("templateId", subtask.templateId || '');
        formData.set("templateName", subtask.templateName || '');
        formData.set("templateQuantity", subtask.templateQuantity || '');

        // Append material, labor, subcontractor
        material.forEach(mat => formData.append("material[]", JSON.stringify(mat)));
        laborData.forEach(lab => formData.append("labor[]", JSON.stringify(lab)));
        subcontractorData.forEach(sub => formData.append("subcontractor[]", JSON.stringify(sub)));

        if (material.length === 0) formData.append("material[]", null);

        if (type === "addsubtask") {
            return await dispatch(addSubTask(formData, id));
        } else if (type === "updateSubTask") {
            return await dispatch(updateSubTask(formData, id));
        }
    };

    return {
        // Initialization
        initLoad,
        loadLaborList,
        preFill,

        // Formatting
        formatNewMaterials,
        formatLaborData,
        formatSubcontractorData,

        // Save operations
        submitHandler,
        saveSubTask,
    };
};
